import React, { useEffect, useState } from 'react';
import { FaCheckCircle, FaTimesCircle, FaEye, FaDownload } from 'react-icons/fa';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';

// ============ FIXED: Handle both with and without /api prefix ============
const getBaseURL = () => {
  const url = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const cleanUrl = url.replace(/\/$/, '');
  if (cleanUrl.endsWith('/api')) {
    return cleanUrl;
  }
  return `${cleanUrl}/api`;
};

const API_URL = getBaseURL();

const AdminWalletTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      // ✅ FIXED: Using API_URL with auto /api prefix
      const response = await axios.get(`${API_URL}/admin/wallet-transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data.transactions);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const verifyTransaction = async (id) => {
    try {
      const token = localStorage.getItem('token');
      // ✅ FIXED: Using API_URL with auto /api prefix
      await axios.put(`${API_URL}/wallet/verify-transaction/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transaction verified successfully');
      fetchTransactions();
      setShowModal(false);
    } catch (error) {
      toast.error('Verification failed');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Wallet Transactions - Admin</title></Helmet>
      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">Wallet Transactions</h1>
            <p className="text-gray-600 mt-1">Verify and manage all wallet recharge requests</p>
          </div>

          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">UID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">No pending transactions</td>
                    </tr>
                  ) : (
                    transactions.map((txn, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4">
                          <div className="font-medium">{txn.userName}</div>
                          <div className="text-xs text-gray-500">{txn.userEmail}</div>
                        </td>
                        <td className="px-6 py-4 font-semibold text-green-600">₹{txn.amount.toLocaleString()}</td>
                        <td className="px-6 py-4 font-mono text-sm">{txn.uid}</td>
                        <td className="px-6 py-4 text-sm">{new Date(txn.date).toLocaleDateString()}</td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Pending</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button onClick={() => { setSelectedTxn(txn); setShowModal(true); }} className="text-blue-600 hover:text-blue-700">
                              <FaEye />
                            </button>
                            <button onClick={() => verifyTransaction(txn.id)} className="text-green-600 hover:text-green-700">
                              <FaCheckCircle />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedTxn && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Transaction Details</h2>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <p className="text-gray-500 text-sm">User</p>
                <p className="font-semibold">{selectedTxn.userName}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Amount</p>
                <p className="text-2xl font-bold text-green-600">₹{selectedTxn.amount.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Transaction UID</p>
                <p className="font-mono">{selectedTxn.uid}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Description</p>
                <p>{selectedTxn.description}</p>
              </div>
              {selectedTxn.screenshot && (
                <div>
                  <p className="text-gray-500 text-sm mb-2">Screenshot</p>
                  <img src={selectedTxn.screenshot} alt="Payment Proof" className="max-h-48 rounded-lg border" />
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button onClick={() => verifyTransaction(selectedTxn.id)} className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700">
                  Verify Transaction
                </button>
                <button onClick={() => setShowModal(false)} className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminWalletTransactions;